import { useEffect, useMemo, useRef } from "react";
import CustomDropdown from "../shared/dropdown/CustomDropdown";
import { formatDistributorLabel } from "../../helpers/vaInvoiceUtils";

const SuggestedBadge = () => (
  <span className="order-hospital-suggest__badge">Suggested</span>
);

export default function DistributorSelect({
  value,
  onChange,
  options = [],
  suggestedOptions = [],
  showWarning = false,
  warningMessage = "",
  facilityName = "",
  hospitalName = "",
  hospitalId = "",
  suggestedHospitalIds = [],
  error = false,
  hideTitle = false,
}) {
  const appliedSuggestionRef = useRef(false);

  const suggestionIds = useMemo(
    () =>
      new Set(
        (Array.isArray(suggestedOptions) ? suggestedOptions : [])
          .map((opt) => String(opt?.id || "").trim())
          .filter(Boolean)
      ),
    [suggestedOptions]
  );

  const suggestedHospitalIdSet = useMemo(
    () =>
      new Set(
        (Array.isArray(suggestedHospitalIds) ? suggestedHospitalIds : [])
          .map((id) => String(id || "").trim())
          .filter(Boolean)
      ),
    [suggestedHospitalIds]
  );

  const soleSuggestedId = useMemo(() => {
    if (suggestionIds.size !== 1) return "";
    return [...suggestionIds][0];
  }, [suggestionIds]);

  const selectOptions = useMemo(
    () =>
      (Array.isArray(options) ? options : [])
        .filter((opt) => opt?.id)
        .map((opt) => {
          const id = String(opt.id);
          const isSuggested = suggestionIds.has(id);
          return {
            value: id,
            label: formatDistributorLabel(opt),
            icon: isSuggested ? <SuggestedBadge /> : null,
          };
        }),
    [options, suggestionIds]
  );

  const selectedOption = useMemo(
    () =>
      (Array.isArray(options) ? options : []).find(
        (opt) => String(opt?.id) === String(value || "")
      ),
    [options, value]
  );

  const selectedCoversHospital = useMemo(
    () => Boolean(value) && suggestionIds.has(String(value)),
    [value, suggestionIds]
  );

  // True only when selected hospital is one of the invoice facility suggestions.
  const hospitalMatchesInvoice = useMemo(() => {
    const id = String(hospitalId || "").trim();
    if (id && suggestedHospitalIdSet.has(id)) return true;

    const facility = String(facilityName || "").trim().toLowerCase();
    const hospital = String(hospitalName || "").trim().toLowerCase();
    if (!facility || !hospital) return false;
    return (
      facility === hospital ||
      facility.includes(hospital) ||
      hospital.includes(facility)
    );
  }, [hospitalId, suggestedHospitalIdSet, facilityName, hospitalName]);

  /**
   * "Match found" only when BOTH are true:
   * 1) selected hospital matches the invoice facility
   * 2) selected VA covers that hospital
   */
  const matchSummary = useMemo(() => {
    if (!selectedCoversHospital || !selectedOption) return null;
    if (!hospitalMatchesInvoice) return null;

    const facility = String(facilityName || "").trim();
    const hospital = String(hospitalName || "").trim();
    const distributor = selectedOption.name || "";
    if (!distributor || !facility) return null;

    return {
      facility,
      hospital: hospital || facility,
      distributor,
    };
  }, [
    selectedCoversHospital,
    selectedOption,
    hospitalMatchesInvoice,
    facilityName,
    hospitalName,
  ]);

  useEffect(() => {
    appliedSuggestionRef.current = false;
  }, [soleSuggestedId]);

  useEffect(() => {
    if (appliedSuggestionRef.current) return;
    if (!soleSuggestedId || !selectOptions.length) return;
    const exists = selectOptions.some(
      (opt) => String(opt.value) === soleSuggestedId
    );
    if (!exists) return;
    appliedSuggestionRef.current = true;
    if (String(value || "") !== soleSuggestedId) onChange?.(soleSuggestedId);
  }, [soleSuggestedId, selectOptions, value, onChange]);

  const handleSelect = (opt) => {
    appliedSuggestionRef.current = true;
    onChange?.(opt?.value);
  };

  return (
    <div className="order-from-section">
      {!hideTitle ? (
        <h6 className="order-from-section__title">
          Distributor <span className="text-danger">*</span>
        </h6>
      ) : null}

      <div className="order-from-section__row">
        <label className="order-from-section__label">Distributor:</label>
        <div className="order-from-section__field">
          <CustomDropdown
            label="Select distributor"
            value={value ? String(value) : ""}
            onSelect={handleSelect}
            options={selectOptions}
            error={error}
            className="w-100"
            searchable={selectOptions.length > 8}
            searchPlaceholder="Search distributor…"
          />
        </div>
      </div>

      {showWarning && (
        <p className="va-field-hint va-field-hint--warning small mb-0 mt-2">
          {warningMessage ||
            "No distributor for this hospital — select manually."}
        </p>
      )}

      {matchSummary && !showWarning ? (
        <div className="order-from-match" role="status">
          <div className="order-from-match__badge">Match found</div>
          <p className="order-from-match__text mb-0">
            Invoice facility <strong>{matchSummary.facility}</strong>
            {matchSummary.hospital &&
            matchSummary.hospital !== matchSummary.facility ? (
              <>
                {" "}
                mapped to <strong>{matchSummary.hospital}</strong>
              </>
            ) : (
              <> was matched in the catalog</>
            )}
            .
          </p>
          <p className="order-from-match__cover mb-0">
            Covered by <strong>{matchSummary.distributor}</strong>
          </p>
        </div>
      ) : null}
    </div>
  );
}
